import { INLINES, Node } from "@contentful/rich-text-types"
import { graphql, useStaticQuery } from "gatsby"
import React, { ReactNode } from "react"
import styled from "styled-components"
import { resolveIsEmail } from "../../../contentful/options/rich-text"
import { useLocalizationContext } from "../../../context/localization.context"
import { generateSources } from "../../../helpers/images"
import RichText from "../../../resolvers/rich-text/rich-text.resolver"
import {
  ContentfulSection,
  QueryResultFlat,
  SingleQueryResult,
  AppFunctionComponent,
} from "../../../types"
import { TextLink } from "../../../ui/components/link/link.component"
import {
  PrimaryHeading,
  SecondaryHeading,
} from "../../common/heading.component"
import {
  MediumSection,
  SectionArticle,
  SectionImage,
  SectionLayout,
} from "../../common/sections/section.component"
import AnimatedTexts from "../../common/typography/animated-texts.component"
import ContactData from "./contact-data.component"

const ContactSection = styled(MediumSection)`
  --max-width: 1260;
  background: ${({ theme }) => theme.color.backgroundLightGradient};
  overflow: hidden;

  @media (max-width: ${({ theme }) =>
      theme.breakpoint.mobileHorizontal - 1}px) {
    padding-top: 15px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
    padding: 0;
    grid-template-columns: 1fr 1.2fr;
    border-radius: ${({ theme }) => theme.size.borderRadius};
  }
`

const ContactImage = styled(SectionImage)`
  border-radius: 0;
  @media (max-width: ${({ theme }) =>
      theme.breakpoint.mobileHorizontal - 1}px) {
    height: 50vw;
  }
`

const ContactArticle = styled(SectionArticle)<{ area?: string | undefined }>`
  margin-top: -2em;
  padding-right: 20px;

  @media (min-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
    margin: 20px 0;
  }

  ${PrimaryHeading} {
    margin-bottom: 7px;
    @media (min-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
      margin-bottom: 10px;
    }
  }

  ${SecondaryHeading} {
    font-weight: 400;
    margin-bottom: 30px;
    @media (min-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
      margin-top: 0px;
      margin-bottom: 15px;
    }
  }
`

const ContactInfo = styled.div`
  flex: 1;
  width: 100%;
  display: flex;
  margin-top: 60px;
  max-width: 480px;
  margin-bottom: 120px;
  flex-direction: column;
  box-sizing: border-box;

  > p {
    display: none;
  }

  @media (min-width: 480px) {
    flex-direction: row;
    justify-content: space-between;
  }

  @media (min-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
    margin-bottom: 0;
    padding-right: 50px;
    flex-direction: column;
  }

  @media (min-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
    flex-direction: row;
  }
`

const Contact: AppFunctionComponent = () => {
  const {
    content: { nodes },
  }: SingleQueryResult<QueryResultFlat<ContentfulSection>> =
    useStaticQuery(query)
  const { locale } = useLocalizationContext()
  const translatedContent =
    nodes.find((node) => node.node_locale === locale) || nodes[0]
  const { title, desktopImage, mobileImage, sectionElement, tabletImage } =
    translatedContent

  return (
    <>
      <ContactSection layout={SectionLayout.ImageText}>
        <ContactImage
          image={generateSources([
            [desktopImage],
            [tabletImage, 1160],
            [mobileImage, 500],
          ])}
          disableShadow
          loading="eager"
        />
        <ContactArticle>
          <PrimaryHeading renderAs={"h1"}>{title}</PrimaryHeading>
          {sectionElement && (
            <>
              <SecondaryHeading renderAs={"h2"}>
                {sectionElement[0]!.title}
                <AnimatedTexts
                  texts={sectionElement[0]!.list || []}
                  delay={2000}
                />
              </SecondaryHeading>
              {
                <RichText
                  content={sectionElement[0]!.text!}
                  options={{
                    [INLINES.HYPERLINK]: (node: Node, children: ReactNode) => (
                      <TextLink to={resolveIsEmail(node.data.uri)}>
                        {children}
                      </TextLink>
                    ),
                  }}
                />
              }
            </>
          )}
          <ContactInfo>
            {translatedContent && <ContactData element={translatedContent} />}
          </ContactInfo>
        </ContactArticle>
      </ContactSection>
    </>
  )
}

export default Contact

const query = graphql`
  query ContactSectionQuery {
    content: allContentfulSection(
      filter: { contentfulid: { eq: "about/contact" } }
    ) {
      nodes {
        node_locale
        title
        text {
          raw
          references {
            ... on ContentfulSectionElement {
              id
              contentful_id
              __typename
              slug
              text {
                raw
              }
            }
          }
        }
        mobileImage: image {
          gatsbyImageData(
            layout: CONSTRAINED
            placeholder: BLURRED
            quality: 100
            width: 470
          )
        }
        tabletImage: image {
          gatsbyImageData(
            layout: CONSTRAINED
            placeholder: BLURRED
            quality: 100
            width: 500
          )
        }
        desktopImage: image {
          gatsbyImageData(
            layout: CONSTRAINED
            placeholder: BLURRED
            quality: 100
            width: 540
          )
        }
        sectionElement {
          title
          list
          text {
            raw
          }
        }
      }
    }
  }
`
